'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';

/**
 * False until the component's first frame has painted, then true for good. A
 * class or style switched on it transitions from what first painted (zeros
 * rolling up, a wash fading out, dots morphing into a check); one set before
 * the first paint just shows.
 */
export function useAfterPaint() {
  const $ = _c(2);
  const [painted, setPainted] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      let frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => setPainted(true));
      });
      return () => cancelAnimationFrame(frame);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return painted;
}

/**
 * True once `value` has changed since the component mounted: a change the
 * viewer saw happen, as opposed to a state the page opened in.
 */
export function useChangedInView(value) {
  const [mounted] = useState(value);
  const [changed, setChanged] = useState(false);
  if (!changed && !Object.is(value, mounted)) {
    setChanged(true);
  }
  return changed;
}

/**
 * True for `holdMs` after `value` changes in a way `matches(previous, next)`
 * accepts; another accepted change starts the hold over. Any other change ends
 * the hold early, and the value a component mounts with never holds, so a
 * reload does not replay the moment.
 */
export function useChangeHold(value, t0) {
  const $ = _c(4);
  const {
    matches,
    holdMs
  } = t0;
  const [previous, setPrevious] = useState(value);
  const [hold, setHold] = useState(0);
  if (!Object.is(value, previous)) {
    setPrevious(value);
    setHold(matches(previous, value) ? hold + 1 : 0);
  }
  let t1;
  let t2;
  if ($[0] !== hold || $[1] !== holdMs) {
    t1 = () => {
      if (hold === 0) {
        return;
      }
      const timer = setTimeout(() => setHold(0), holdMs);
      return () => clearTimeout(timer);
    };
    t2 = [hold, holdMs];
    $[0] = hold;
    $[1] = holdMs;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return hold !== 0;
}